/**
 * NOCCA — Taille en rupture : bascule sur une couleur encore disponible
 *
 * Quand le client clique une taille épuisée dans la couleur affichée (le L en
 * noir), le thème d'origine garde la combinaison morte et grise le bouton
 * d'ajout au panier. Ici on garde la taille cliquée et on bascule la couleur
 * sur celle qui a encore du stock. Sans rien annoncer : la page montre déjà la
 * pastille et les photos de la nouvelle couleur.
 *
 * Le sens de la bascule n'est jamais inversé : on ne change que la couleur,
 * jamais la taille. Quelqu'un qui clique « L » veut du L, pas du M.
 *
 * L'écouteur est en phase de capture pour passer AVANT celui de
 * `variant-selects` (assets/global.js) : quand le thème lit les options
 * cochées pour recharger la section, la couleur est déjà corrigée. Rien à
 * modifier dans les fichiers du thème d'origine.
 */
(function () {
  'use strict';

  // Le snippet peut être rendu plusieurs fois sur une même page (fiche
  // produit + ajout rapide). Un seul écouteur suffit.
  if (window.noccaTailleRupture) return;
  window.noccaTailleRupture = true;

  /* --------------------------------------------------------- Réglages */

  // Les options que l'on s'autorise à changer à la place du client.
  // Reconnues aussi par la classe `--swatch` du thème (pastilles de couleur).
  var SWAPPABLE_LABEL = /couleur|color|colour/i;

  /* ------------------------------------------------------------ Outils */

  var GROUP_SELECTOR = '.product-form__input';

  function toArray(list) {
    return Array.prototype.slice.call(list);
  }

  function getData(picker) {
    var scope = picker.closest('product-info') || picker.closest('.shopify-section');
    var script = scope ? scope.querySelector('[data-nocca-rupture-data]') : null;
    if (!script) return null;

    if (script.noccaRuptureData !== undefined) return script.noccaRuptureData;
    script.noccaRuptureData = null;

    try {
      var parsed = JSON.parse(script.textContent);
      if (parsed && parsed.variants && parsed.variants.length) {
        script.noccaRuptureData = {
          size: parsed.variants[0].options.length,
          available: parsed.variants.reduce(function (index, variant) {
            index[keyOf(variant.options)] = variant.available === true;
            return index;
          }, {}),
        };
      }
    } catch (error) {
      script.noccaRuptureData = null;
    }

    return script.noccaRuptureData;
  }

  // Les valeurs d'options ne contiennent pas de saut de ligne : le séparateur
  // ne peut pas entrer en collision avec une valeur.
  function keyOf(values) {
    return values.join('\n');
  }

  // « Couleur: Black » dans une légende à pastilles, « Taille » dans une pilule.
  function optionName(group) {
    var label = group.querySelector('legend, .form__label');
    if (!label) return '';
    return label.textContent.split(':')[0].trim();
  }

  function isSwappable(group) {
    return (
      group.classList.contains('product-form__input--swatch') ||
      SWAPPABLE_LABEL.test(optionName(group))
    );
  }

  function selectedValue(group) {
    var checked = group.querySelector('input[type="radio"]:checked');
    if (checked) return checked.value;

    var select = group.querySelector('select');
    return select ? select.value : null;
  }

  function offeredValues(group) {
    var radios = group.querySelectorAll('input[type="radio"]');
    if (radios.length) {
      return toArray(radios).map(function (input) {
        return input.value;
      });
    }

    var select = group.querySelector('select');
    if (!select) return [];

    return toArray(select.options).map(function (option) {
      return option.value;
    });
  }

  // Met à jour la valeur affichée dans la légende, le temps que la section
  // rechargée par le thème prenne le relais. On laisse tranquille le menu
  // déroulant à pastille, dont le contenu est un élément et non du texte.
  function syncLabel(group, value) {
    var display = group.querySelector('[data-selected-value]');
    if (display && !display.firstElementChild) display.textContent = value;
  }

  function applyValue(group, value) {
    var radios = toArray(group.querySelectorAll('input[type="radio"]'));

    for (var i = 0; i < radios.length; i++) {
      if (radios[i].value !== value) continue;
      radios[i].checked = true;
      syncLabel(group, value);
      return true;
    }

    var select = group.querySelector('select');
    if (!select) return false;

    var options = toArray(select.options);
    var target = null;

    for (var j = 0; j < options.length; j++) {
      if (options[j].value === value) target = options[j];
    }
    if (!target) return false;

    // `variant-selects` lit l'attribut `selected`, pas la propriété.
    options.forEach(function (option) {
      option.removeAttribute('selected');
    });
    target.setAttribute('selected', 'selected');
    select.value = value;
    syncLabel(group, value);
    return true;
  }

  /* ---------------------------------------------------------- Bascule */

  function handleChange(event) {
    var target = event.target;
    if (!target || !target.matches || !target.matches('input[type="radio"], select')) return;

    var picker = target.closest('variant-selects');
    if (!picker) return;

    var data = getData(picker);
    if (!data) return;

    var groups = toArray(picker.querySelectorAll(GROUP_SELECTOR));
    if (groups.length !== data.size) return;

    var selection = [];
    var changed = -1;

    for (var i = 0; i < groups.length; i++) {
      if (groups[i].contains(target)) changed = i;
      var value = selectedValue(groups[i]);
      if (value === null) return;
      selection.push(value);
    }
    if (changed === -1) return;

    // La combinaison demandée existe et est en stock : rien à faire.
    if (data.available[keyOf(selection)]) return;

    for (var j = 0; j < groups.length; j++) {
      if (j === changed || !isSwappable(groups[j])) continue;

      var candidates = offeredValues(groups[j]);

      for (var k = 0; k < candidates.length; k++) {
        if (candidates[k] === selection[j]) continue;

        var attempt = selection.slice();
        attempt[j] = candidates[k];
        if (!data.available[keyOf(attempt)]) continue;
        if (applyValue(groups[j], candidates[k])) return;
      }
    }

    // Épuisé dans toutes les couleurs : le thème affiche « Rupture de stock ».
  }

  document.addEventListener('change', handleChange, true);
})();
